import { authErrorMessage } from "@studyward/shared";
import { Button, ErrorMessage, Field, Skeleton, buttonClass } from "@studyward/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { AuthFrame, errorText } from "../components/ui";
import { authStorage, supabase } from "../lib/api";
import { env } from "../lib/env";
import { signOut, useAuth } from "../lib/session";

/** Step 1 (§8.1): email + password. No social sign-in on the admin host. */
export function PasswordStep() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(() => authStorage.getRememberMe());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    authStorage.setRememberMe(rememberMe);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setBusy(false);
    if (error) setError(authErrorMessage(error));
  };

  return (
    <AuthFrame title="Admin sign-in" subtitle="Sign in with your StudyWard account. You'll confirm it's you with your authenticator app next.">
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label="Email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Field label="Password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <label className="flex items-center gap-2 cursor-pointer text-small text-text-secondary select-none">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="size-4 rounded accent-primary cursor-pointer"
          />
          <span>Remember me for 30 days</span>
        </label>
        {error && <ErrorMessage message={error} />}
        <Button type="submit" loading={busy} disabled={!email || !password}>
          Continue
        </Button>
      </form>
      <a className="text-center text-small font-semibold text-text-link underline" href={`${env.appUrl}/signin`}>
        Forgot your password? Reset it on the StudyWard app
      </a>
    </AuthFrame>
  );
}

const TOTP_FRIENDLY_NAME = "StudyWard Admin";

/**
 * Step 2 (§8.1): every admin enrols a TOTP authenticator on first sign-in, then verifies a code on every sign-in.
 * Only a verified factor lifts the session to aal2.
 */
export function MfaStep() {
  const { refresh } = useAuth();
  const queryClient = useQueryClient();
  const factors = useQuery({
    queryKey: ["mfa", "factors"],
    queryFn: async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      return data;
    },
  });
  const verified = factors.data?.totp.find((f) => f.status === "verified") ?? null;

  const enrolment = useQuery({
    queryKey: ["mfa", "enrol"],
    enabled: factors.isSuccess && !verified,
    staleTime: Infinity,
    gcTime: 0,
    queryFn: async () => {
      // A half-finished enrolment from an earlier visit blocks a new one with the same name.
      for (const f of factors.data?.all ?? []) {
        if (f.status === "unverified") await supabase.auth.mfa.unenroll({ factorId: f.id });
      }
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp", friendlyName: TOTP_FRIENDLY_NAME });
      if (error) throw error;
      return data;
    },
  });

  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const factorId = verified?.id ?? enrolment.data?.id ?? null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!factorId) return;
    setBusy(true);
    setError(null);
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code: code.replace(/\s/g, "") });
    setBusy(false);
    if (error) {
      setError("That code didn't match. Check the time on your phone and enter the newest code.");
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["mfa"] });
    await refresh();
  };

  const loading = factors.isPending || (!verified && enrolment.isPending);
  const failure = factors.error ?? enrolment.error;

  return (
    <AuthFrame
      title={verified ? "Enter your code" : "Set up your authenticator"}
      subtitle={
        verified
          ? "Open your authenticator app and enter the 6-digit code for StudyWard Admin."
          : "The admin console needs a second step at every sign-in. Scan this code with an authenticator app (such as Google Authenticator, 1Password or Authy), then enter the 6-digit code it shows."
      }
    >
      {loading && <Skeleton className="h-56 w-full" label="Preparing your authenticator" />}
      {failure && <ErrorMessage message={errorText(failure)} onRetry={() => void factors.refetch()} />}
      {!loading && !failure && (
        <>
          {!verified && enrolment.data && (
            <div className="flex flex-col items-center gap-3 rounded-card bg-surface-card p-4 shadow-card">
              <img src={enrolment.data.totp.qr_code} alt="QR code to add StudyWard Admin to your authenticator app" width={192} height={192} className="size-48" />
              <p className="text-center text-caption text-text-secondary">
                Can't scan it? Enter this key instead:
                <br />
                <code className="break-all font-semibold text-text-primary">{enrolment.data.totp.secret}</code>
              </p>
            </div>
          )}
          <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
            <Field
              label="6-digit code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9 ]*"
              maxLength={7}
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              error={error}
            />
            <Button type="submit" loading={busy} disabled={code.replace(/\s/g, "").length !== 6}>
              {verified ? "Verify" : "Finish setup"}
            </Button>
          </form>
        </>
      )}
      <p className="text-center text-caption text-text-secondary">Lost your authenticator? Ask an owner to reset it for you.</p>
      <button type="button" onClick={() => void signOut()} className={buttonClass("ghost", "sm")}>
        Use a different account
      </button>
    </AuthFrame>
  );
}

/** Signed in with MFA, but the account has no admin role — a hard denial, never a hidden-but-working UI (§10.3). */
export function NoAccess({ message }: { message: string }) {
  return (
    <AuthFrame title="No admin access" subtitle={message}>
      <p className="text-small text-text-secondary">If you should have access, ask an owner to add you to the admin team.</p>
      <a className={buttonClass("secondary")} href={env.appUrl}>
        Go to the StudyWard app
      </a>
      <Button variant="ghost" size="sm" onClick={() => void signOut()}>
        Sign out
      </Button>
    </AuthFrame>
  );
}
